// Calendar helpers for the report comparison affordances. Dates are plain
// `YYYY-MM-DD` strings and all arithmetic is whole-day UTC, so no timezone or
// floating-point drift can creep in.

const DAY_MS = 86_400_000;

function parse(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(isoDay: string, days: number): string {
  const date = parse(isoDay);
  if (!date) return isoDay;
  return iso(new Date(date.getTime() + days * DAY_MS));
}

/**
 * The equal-length range immediately before `[start, end]`. Used by the
 * profit-and-loss "compare to previous period" affordance.
 */
export function previousRange(
  start: string,
  end: string,
): { start: string; end: string } | null {
  const startDate = parse(start);
  const endDate = parse(end);
  if (!startDate || !endDate || endDate < startDate) return null;
  const lengthDays = Math.round((endDate.getTime() - startDate.getTime()) / DAY_MS) + 1;
  const previousEnd = addDays(start, -1);
  const previousStart = addDays(previousEnd, -(lengthDays - 1));
  return { start: previousStart, end: previousEnd };
}

/** The same calendar date one year earlier (as-at statements' quick compare). */
export function sameDayLastYear(isoDay: string): string | null {
  const date = parse(isoDay);
  if (!date) return null;
  return `${String(date.getUTCFullYear() - 1).padStart(4, "0")}-${String(
    date.getUTCMonth() + 1,
  ).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}
